import React, { ReactNode } from "react";
import {
  IconButton,
  Typography,
  Box,
  useMediaQuery,
  Theme,
} from "@mui/material";
import MuiCloseIcon from "@mui/icons-material/Close";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";
import isPropValid from "@emotion/is-prop-valid";
import { useScrollTrigger } from "@mui/material";
import { useMandatorContext } from "@bn/theming";
import {
  ARIVA_HEADER_HEIGHT_DESKTOP,
  ARIVA_HEADER_HIDE_SCROLL_THRESHOLD,
} from "@components/ArivaHeader/arivaHeaderConfig";
import { Logo } from "@components/Logo";

export type Variant = "light" | "dark";

const Container = styled(Box, {
  shouldForwardProp: (prop: string) =>
    isPropValid(prop) || prop === "boxShadow" || prop === "justifyContent",
})`
  ${({ backgroundColor }: { backgroundColor?: string }) =>
    backgroundColor && `background-color: ${backgroundColor};`}
  width: 100%;
  display: flex;
  align-items: center;
  padding-right: ${({ theme }) => theme.spacing(4)};
  padding-left: ${({ theme }) => theme.spacing(4)};
  height: 48px;
  border-radius: ${({ theme }) => theme.shape.borderRadius * 1.2}px;
  box-shadow: ${({ theme }) => theme.shadows[1]};
`;

const Title = styled(Typography, {
  shouldForwardProp: (name) => name !== "textColor",
})`
  ${({ textColor }: { textColor?: string; component: string }) =>
    textColor && `color: ${textColor};`}
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  ${({ theme }) => theme.breakpoints.down("sm")} {
    position: relative;
    top: auto;
    left: auto;
    transform: none;
  }
`;

const CloseIcon = styled(MuiCloseIcon, { shouldForwardProp: isPropValid })`
  ${({ textColor }: { textColor?: string }) =>
    textColor && `color: ${textColor};`}
`;

export interface HeaderProps {
  backUrl?: string;
  onBackClick?: () => void;
  title?: ReactNode;
  variant?: Variant;
  onLogoClick?: () => void;
  isCenteredLayout?: boolean;
}

export const WizardsHeader = ({
  backUrl,
  title,
  variant = "light",
  onBackClick,
  onLogoClick,
  isCenteredLayout,
}: HeaderProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { isAriva } = useMandatorContext();
  // Mirrors the Ariva header: when scrolled past the threshold the header
  // hides, so the close button moves up to take its place.
  const arivaHeaderHidden = useScrollTrigger({
    disableHysteresis: true,
    threshold: ARIVA_HEADER_HIDE_SCROLL_THRESHOLD,
  });
  const logoVariant = isXs ? "compact" : "bankConnection";
  const backgroundColor = theme.palette.wizardHeader.header;
  const color = theme.palette.wizardHeader.textOnHeader;

  // On Ariva desktop the standalone header sits on top of these full-page
  // flows, so the full in-app wizard header would be a duplicate. Keep only a
  // close button (top-right) so the user can still leave the flow. On mobile
  // the Ariva header isn't shown, so keep the full header for navigation.
  if (isAriva && !isXs) {
    if (!backUrl && !onBackClick) return null;
    return (
      <Box
        sx={{
          position: "fixed",
          // Sit a bit below the header; move up to the top once it hides.
          top: (muiTheme) =>
            arivaHeaderHidden
              ? muiTheme.spacing(2)
              : `calc(${ARIVA_HEADER_HEIGHT_DESKTOP}px + ${muiTheme.spacing(2)})`,
          right: (muiTheme) => muiTheme.spacing(4),
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
          transition: "top 0.3s ease",
        }}
      >
        <IconButton
          {...(backUrl && !onBackClick
            ? { href: backUrl }
            : { onClick: onBackClick })}
          size="small"
          aria-label="close"
        >
          <CloseIcon textColor={theme.palette.text.primary} />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box px={4} pt={4}>
      <Container
        backgroundColor={backgroundColor}
        justifyContent={
          isCenteredLayout
            ? "center"
            : title || backUrl || onBackClick
              ? "space-between"
              : "center"
        }
      >
        {isCenteredLayout ? (
          <>
            <Box display="flex" flexGrow={0.5} justifyContent="end">
              <Logo variant="bankConnection" onClick={onLogoClick} />
            </Box>
            <Box display="flex" flexGrow={0.5} justifyContent="end">
              <IconButton onClick={onBackClick} size="large">
                <CloseIcon textColor={color} />
              </IconButton>
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Logo variant={logoVariant} onClick={onLogoClick} />
            </Box>
            {title && (
              <Title textColor={color} component="div">
                {title}
              </Title>
            )}
            {backUrl && !onBackClick && (
              <IconButton
                href={backUrl}
                size="small"
                sx={{
                  padding: 0,
                }}
              >
                <CloseIcon textColor={color} />
              </IconButton>
            )}
            {!backUrl && onBackClick && (
              <IconButton
                onClick={onBackClick}
                size="small"
                sx={{
                  padding: 0,
                }}
              >
                <CloseIcon textColor={color} />
              </IconButton>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};
